import { type mobileNavItemProps, type sideMenuItemProps } from "routes/routeData";

import { doRestrictionsOverlap } from "./security-restrictions";

export const filterMenuItemsForUserSubset = (
  menuItem: sideMenuItemProps | mobileNavItemProps,
  securityRestrictions: string[],
) => {
  const shouldItemBeHidden = doRestrictionsOverlap(securityRestrictions, menuItem.hiddenFor || []);
  return shouldItemBeHidden ? false : menuItem;
};

export const tzOffsetInMinutes = (ianaTimeZone: string) => {
  try {
    const now = new Date();
    now.setSeconds(0, 0);

    const tzDateString = now.toLocaleString("en-US", {
      timeZone: ianaTimeZone,
      hourCycle: "h23",
    });

    const isoRegex = /(\d+)\/(\d+)\/(\d+), (\d+):(\d+)/;

    const match = isoRegex.exec(tzDateString);
    // match returns an array with the following vals:
    // [ MM/DD/YYYY, HH:mm <string>,
    //   mm<string>,
    //   dd<string>,
    //   yyyy<string>,
    //   HH<string>,
    //   mm<string>,
    //   ...some other attributes we don't really care about.
    // ]

    if (match) {
      // Since this IS javascript, we don't technically need to do any
      // type coercion and it _should_ work, but it's probably a good
      // idea to try to avoid as many surprises as possible
      const [, month, day, year, hour, min] = match.map(Number);

      // UTC time in millis
      const tzTime = Date.UTC(year, month - 1, day, hour, min);

      // Calculates the difference in minutes to get the offset value
      return Math.floor((tzTime - now.getTime()) / (1000 * 60));
    }
  } catch {
    return undefined;
  }
};
/**
 * try / catch TS error handling
 * https://kentcdodds.com/blog/get-a-catch-block-error-message-with-typescript
 */

type ErrorWithMessage = {
  message: string;
};

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as Record<string, unknown>).message === "string"
  );
}

function toErrorWithMessage(maybeError: unknown): ErrorWithMessage {
  if (isErrorWithMessage(maybeError)) return maybeError;
  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    // fallback in case there's an error stringifying the maybeError
    // like with circular references for example.
    return new Error(String(maybeError));
  }
}

export function getErrorMessage(error: unknown) {
  return toErrorWithMessage(error).message;
}

export function truncateString(string = "", cutoff: number) {
  if (string.length > cutoff) {
    return `${string.slice(0, cutoff)}...`;
  } else {
    return string;
  }
}

export const formatTimeAmPm = (time = "") => {
  // Remove periods from a.m./p.m. to normalize format
  const timeLowercased = time.toLowerCase().replace(/\./g, "");
  const timeSplit = timeLowercased.split(" ");
  let start = timeSplit[0];
  let end = timeSplit[timeSplit.length - 1];

  // [ '8:00am', '-', '4:30pm' ] or [ '1:00pm', '-', '4:30pm' ]
  if (start.includes("am") || start.includes("pm")) {
    start = start.replace("am", " am");
    start = start.replace("pm", " pm");
  } else {
    // ["8:00", "am", "-", "4:30pm"] or ["1:00", "pm", "-", "4:30pm"]
    start = `${start} ${timeSplit[1]}`;
  }

  // [ '8:00am' ] or [ '8:00', `am` ]
  if (timeSplit.length <= 2) {
    // x:xx am or xx:xx am
    return start;
  }

  // ["8:00am", "-", "4:30pm"]
  if ((end.includes("am") || end.includes("pm")) && end.length > 2) {
    end = end.replace("am", " am");
    end = end.replace("pm", " pm");
  } else {
    // ["8:00am", "-", "4:30", "pm"] or ["1:00pm", "-", "4:30", "pm"]
    end = `${timeSplit[timeSplit.length - 2]} ${end}`;
  }

  // x:xx am - x:xx pm or xx:xx am - xx:xx pm
  return `${start} - ${end}`;
};

export function shallowEqualArrays<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;

  // Make a copy so we can mark matches
  const bUsed = new Array(b.length).fill(false);

  return a.every((itemA) => {
    // Find the first unused index in b that strictly equals itemA
    for (let i = 0; i < b.length; i++) {
      if (!bUsed[i] && itemA === b[i]) {
        bUsed[i] = true;
        return true;
      }
    }
    return false;
  });
}
